import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import moment from 'moment';
import { DATE_FORMAT } from 'framework/utils';
import { ProjectReviewGroup } from '../../types';
import { formatEnumLabel, getEntityCounts } from '../utils';
import CountBadges from './CountBadges';
import ReviewItemRow from './ReviewItemRow';

const getLastUpdate = (group: ProjectReviewGroup): Nullable<string> => {
  const occurredAtValues = group.entities.flatMap(entity => entity.changes.map(change => change.occurredAt));

  if (!occurredAtValues.length) return null;

  return occurredAtValues.reduce((latest, occurredAt) => (occurredAt > latest ? occurredAt : latest));
};

interface ReviewSectionAccordionProps {
  comments: Record<string, string>;
  group: ProjectReviewGroup;
  isCommentingDisabled: boolean;
  openComments: Set<string>;
  onCommentChange?: (entityId: string, value: string) => void;
  onCommentToggle?: (entityId: string) => void;
}

const ReviewSectionAccordion = ({
  comments,
  group,
  isCommentingDisabled,
  openComments,
  onCommentChange,
  onCommentToggle,
}: ReviewSectionAccordionProps) => {
  const { changesCount, staleItemsCount } = getEntityCounts(group.entities);
  const lastUpdate = getLastUpdate(group);

  return (
    <Accordion defaultExpanded disableGutters square variant="outlined" sx={{ mb: 1, '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, flexDirection: 'row-reverse', gap: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={2}>
          <Typography variant="subtitle2">{formatEnumLabel(group.entityType)}:</Typography>
          <Box display="flex" alignItems="center" gap={2}>
            {lastUpdate && (
              <Typography variant="body2">
                Last update: {moment(lastUpdate).format(DATE_FORMAT.API_ONLY_DATE)}
              </Typography>
            )}
            <CountBadges changesCount={changesCount} staleItemsCount={staleItemsCount} />
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1, py: 0 }}>
        {group.entities.map(entity => (
          <ReviewItemRow
            key={entity.entityId}
            comment={comments[entity.entityId] ?? ''}
            entity={entity}
            entityType={group.entityType}
            isCommentingDisabled={isCommentingDisabled}
            isCommentOpen={openComments.has(entity.entityId)}
            onCommentChange={onCommentChange && (value => onCommentChange(entity.entityId, value))}
            onCommentToggle={onCommentToggle && (() => onCommentToggle(entity.entityId))}
          />
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

export default ReviewSectionAccordion;
